import React, { useCallback, useEffect, useState } from 'react';
import {
  RiComputerLine,
  RiDeleteBinLine,
  RiSmartphoneLine,
  RiTabletLine,
} from 'react-icons/ri';
import { toast } from 'react-hot-toast';
import styles from '../../styles/UserPage.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDevice(ua) {
  if (!ua) return { label: 'Unknown device', icon: 'desktop' };
  const u = ua.toLowerCase();
  const isMobile = /iphone|android.*mobile|windows phone/.test(u);
  const isTablet = /ipad|android(?!.*mobile)/.test(u);
  const browser =
    u.includes('edg') ? 'Edge'
    : u.includes('chrome') ? 'Chrome'
    : u.includes('firefox') ? 'Firefox'
    : u.includes('safari') ? 'Safari'
    : 'Browser';
  const os =
    u.includes('iphone') ? 'iPhone'
    : u.includes('ipad') ? 'iPad'
    : u.includes('android') ? 'Android'
    : u.includes('windows') ? 'Windows'
    : u.includes('mac') ? 'macOS'
    : u.includes('linux') ? 'Linux'
    : 'Unknown OS';
  return {
    label: `${browser} on ${os}`,
    icon: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
  };
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function DeviceIcon({ type, size = 18, color }) {
  const props = { size, style: { color, flexShrink: 0 } };
  if (type === 'mobile') return <RiSmartphoneLine {...props} />;
  if (type === 'tablet') return <RiTabletLine {...props} />;
  return <RiComputerLine {...props} />;
}

// ---------------------------------------------------------------------------
// Sessions section
//
// Layout:
//   • Current session — always its own row at the top.
//   • Other sessions  — grouped by IP address (same IP = same network).
//     Each IP group is collapsed by default; clicking it expands to show
//     individual session rows with their own Revoke buttons plus a
//     "Revoke all from this IP" shortcut.
//   • Global "Sign out all other sessions" button at the bottom.
// ---------------------------------------------------------------------------

/** Group non-current sessions by ip_address, newest-first within each group. */
function buildIpGroups(sessions) {
  const others = sessions
    .filter((s) => !s.is_current)
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const map = new Map();
  for (const s of others) {
    const key = s.ip_address || 'Unknown IP';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  // Return groups sorted by most-recent session in each group
  return Array.from(map.entries())
    .sort(([, a], [, b]) =>
      new Date(b[0].created_at) - new Date(a[0].created_at)
    )
    .map(([ip, group]) => ({ ip, sessions: group }));
}

function SessionsSection({ API_URL, onLoggedOut }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);   // session_id being revoked
  const [revokingIp, setRevokingIp] = useState(null); // IP whose group is being bulk-revoked
  const [revokingOthers, setRevokingOthers] = useState(false);
  const [expandedIps, setExpandedIps] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/sessions`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      } else {
        toast.error('Failed to load sessions. Please try again.');
      }
    } catch {
      toast.error('Failed to load sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  const toggleIp = useCallback((ip) => {
    setExpandedIps((prev) => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip); else next.add(ip);
      return next;
    });
  }, []);

  /** Revoke a single session by its opaque session_id (never the token). */
  const revoke = useCallback(async (sessionId, isCurrent) => {
    setRevoking(sessionId);
    try {
      const res = await fetch(
        `${API_URL}/api/user/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (res.ok) {
        if (isCurrent) { onLoggedOut(); return; }
        setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      } else {
        toast.error('Failed to revoke session. Please try again.');
      }
    } catch {
      toast.error('Failed to revoke session. Please try again.');
    } finally {
      setRevoking(null);
    }
  }, [API_URL, onLoggedOut]);

  /** Revoke all sessions from a specific IP group sequentially.
   *
   * Session ids that succeed (2xx) or are already gone (404 — session
   * expired or concurrently deleted by another tab) are treated as
   * successfully revoked and removed from local state.  A non-404 server
   * error stops the loop and shows an error toast; remaining un-revoked
   * sessions stay visible so the user can retry.
   *
   * `setSessions` runs in `finally` so successfully-deleted sessions are
   * always cleaned up even when a later `fetch()` throws a network error. */
  const revokeIpGroup = useCallback(async (ip, sessionIds) => {
    setRevokingIp(ip);
    const succeeded = [];
    let hadError = false;
    try {
      for (const sessionId of sessionIds) {
        const res = await fetch(
          `${API_URL}/api/user/sessions/${encodeURIComponent(sessionId)}`,
          { method: 'DELETE', credentials: 'include' },
        );
        if (res.ok || res.status === 404) {
          // 404 = already gone (expired / concurrently deleted) — treat as success
          succeeded.push(sessionId);
        } else {
          hadError = true;
          toast.error('Some sessions could not be revoked. Please try again.');
          break;
        }
      }
    } catch {
      hadError = true;
      toast.error('Failed to revoke sessions. Please try again.');
    } finally {
      // Always sync local state with what the server confirmed is gone,
      // even when a network error interrupted the loop mid-way.
      if (succeeded.length > 0) {
        const revokedSet = new Set(succeeded);
        setSessions((prev) => prev.filter((s) => !revokedSet.has(s.session_id)));
      }
      // Collapse the group only when every session was confirmed removed
      if (!hadError && succeeded.length === sessionIds.length) {
        setExpandedIps((prev) => { const n = new Set(prev); n.delete(ip); return n; });
      }
      setRevokingIp(null);
    }
  }, [API_URL]);

  /** Revoke all non-current sessions via the bulk endpoint. */
  const revokeOthers = async () => {
    setRevokingOthers(true);
    try {
      const res = await fetch(`${API_URL}/api/user/sessions/others`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setSessions((prev) => prev.filter((s) => s.is_current));
        setExpandedIps(new Set());
      } else {
        toast.error('Failed to sign out other devices. Please try again.');
      }
    } catch {
      toast.error('Failed to sign out other devices. Please try again.');
    } finally {
      setRevokingOthers(false);
    }
  };

  const current = sessions.find((s) => s.is_current);
  // Memoize so the Map/sort work only reruns when sessions changes
  const ipGroups = React.useMemo(() => buildIpGroups(sessions), [sessions]);
  const otherCount = sessions.filter((s) => !s.is_current).length;

  // Shared inline-button style
  const inlineBtn = (danger) => ({
    background: 'transparent',
    border: '1px solid var(--input-border)',
    borderRadius: 4,
    padding: '3px 8px',
    cursor: 'pointer',
    color: danger ? '#e5393f' : 'var(--secondary-text-color)',
    fontSize: '0.72rem',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  });

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Active Sessions</h2>
      <p className={styles.sectionDescription}>
        Sessions are grouped by IP address — different IPs mean different
        networks. Click a group to expand and see individual sessions or
        revoke all from that location.
      </p>

      {loading && (
        <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)' }}>
          Loading sessions…
        </p>
      )}

      {!loading && sessions.length === 0 && (
        <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)' }}>
          No active sessions found.
        </p>
      )}

      {!loading && sessions.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>

          {/* ── Current session (always shown individually) ── */}
          {current && (() => {
            const { label, icon } = parseDevice(current.user_agent);
            const busy = revoking === current.session_id;
            return (
              <li style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.625rem 0', borderBottom: '1px solid var(--input-border)',
              }}>
                <DeviceIcon type={icon} size={18}
                  color="var(--primary-color, #636cff)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    flexWrap: 'wrap', fontSize: '0.875rem', fontWeight: 600,
                    color: 'var(--text-color)',
                  }}>
                    {label}
                    <span style={{
                      fontSize: '0.68rem', padding: '1px 7px', borderRadius: 10,
                      background: 'var(--primary-color, #636cff)',
                      color: '#fff', fontWeight: 500,
                    }}>this session</span>
                  </div>
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '0 0.4rem',
                    fontSize: '0.75rem', color: 'var(--secondary-text-color)',
                    marginTop: 2,
                  }}>
                    {current.ip_address && (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                        {current.ip_address}
                      </span>
                    )}
                    {current.ip_address && <span>·</span>}
                    <span>Signed in {formatDate(current.created_at)}</span>
                    <span>·</span>
                    <span>Expires {formatDate(current.expires_at)}</span>
                  </div>
                </div>
                <button
                  onClick={() => revoke(current.session_id, true)}
                  disabled={busy}
                  style={{ ...inlineBtn(true), opacity: busy ? 0.5 : 1 }}
                >
                  <RiDeleteBinLine size={12} />
                  Sign out
                </button>
              </li>
            );
          })()}

          {/* ── Other sessions grouped by IP ── */}
          {ipGroups.map(({ ip, sessions: group }) => {
            const isExpanded = expandedIps.has(ip);
            const isBulkBusy = revokingIp === ip;
            // Representative: most recent session in the group
            const rep = group[0];
            const repDevice = parseDevice(rep.user_agent);
            const uniqueDevices = [...new Set(
              group.map((s) => parseDevice(s.user_agent).label)
            )];

            return (
              <li key={ip} style={{ borderBottom: '1px solid var(--input-border)' }}>
                {/* Collapsed header row — click to expand */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleIp(ip)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); // suppress Space-to-scroll
                      toggleIp(ip);
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.625rem 0', cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <DeviceIcon type={repDevice.icon} size={18}
                    color="var(--secondary-text-color)" />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* IP + session count */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      flexWrap: 'wrap',
                    }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: '0.82rem',
                        fontWeight: 600, color: 'var(--text-color)',
                      }}>
                        {ip}
                      </span>
                      <span style={{
                        fontSize: '0.68rem', padding: '1px 6px', borderRadius: 10,
                        background: 'var(--input-border)',
                        color: 'var(--secondary-text-color)',
                      }}>
                        {group.length} {group.length === 1 ? 'session' : 'sessions'}
                      </span>
                    </div>
                    {/* Device summary + latest date */}
                    <div style={{
                      fontSize: '0.75rem', color: 'var(--secondary-text-color)',
                      marginTop: 2,
                    }}>
                      {uniqueDevices.join(', ')}
                      {' · Last signed in '}{formatDate(rep.created_at)}
                    </div>
                  </div>

                  {/* Expand/collapse chevron */}
                  <span style={{
                    fontSize: '0.75rem', color: 'var(--secondary-text-color)',
                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                    flexShrink: 0,
                  }}>▾</span>
                </div>

                {/* Expanded: individual sessions + bulk revoke */}
                {isExpanded && (
                  <div style={{
                    paddingLeft: '2rem',
                    paddingBottom: '0.5rem',
                    borderTop: '1px dashed var(--input-border)',
                  }}>
                    {/* Bulk revoke button */}
                    <div style={{
                      display: 'flex', justifyContent: 'flex-end',
                      padding: '0.4rem 0 0.25rem',
                    }}>
                      <button
                        onClick={() => revokeIpGroup(ip, group.map((s) => s.session_id))}
                        disabled={isBulkBusy || revoking !== null}
                        style={{
                          ...inlineBtn(true),
                          opacity: (isBulkBusy || revoking !== null) ? 0.5 : 1,
                          cursor: (isBulkBusy || revoking !== null) ? 'default' : 'pointer',
                          fontSize: '0.75rem',
                        }}
                      >
                        <RiDeleteBinLine size={12} />
                        {isBulkBusy
                          ? 'Revoking…'
                          : `Revoke all ${group.length > 1 ? `(${group.length})` : ''} from this IP`}
                      </button>
                    </div>

                    {/* Individual session rows */}
                    {group.map((s) => {
                      const { label, icon } = parseDevice(s.user_agent);
                      const busy = revoking === s.session_id;
                      return (
                        <div key={s.session_id} style={{
                          display: 'flex', alignItems: 'center',
                          gap: '0.5rem', padding: '0.4rem 0',
                          borderTop: '1px solid var(--input-border)',
                        }}>
                          <DeviceIcon type={icon} size={14}
                            color="var(--secondary-text-color)" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '0.8rem', color: 'var(--text-color)',
                            }}>
                              {label}
                              {s.token_hint && (
                                <span style={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.7rem',
                                  color: 'var(--secondary-text-color)',
                                  marginLeft: '0.4rem',
                                }}>
                                  ····{s.token_hint}
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '0.72rem',
                              color: 'var(--secondary-text-color)',
                            }}>
                              Signed in {formatDate(s.created_at)}
                              {' · Expires '}{formatDate(s.expires_at)}
                            </div>
                          </div>
                          <button
                            onClick={() => revoke(s.session_id, false)}
                            disabled={busy || isBulkBusy}
                            style={{
                              ...inlineBtn(false),
                              opacity: (busy || isBulkBusy) ? 0.4 : 1,
                              cursor: (busy || isBulkBusy) ? 'default' : 'pointer',
                            }}
                          >
                            <RiDeleteBinLine size={11} />
                            {busy ? 'Revoking…' : 'Revoke'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Global sign-out-all button */}
      {!loading && otherCount > 0 && (
        <button
          onClick={revokeOthers}
          disabled={revokingOthers}
          className={styles.button}
          style={{
            marginTop: '1rem',
            backgroundColor: '#e5393f',
            opacity: revokingOthers ? 0.6 : 1,
          }}
        >
          {revokingOthers
            ? 'Signing out…'
            : `Sign out all other sessions (${otherCount})`}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privacy section
// ---------------------------------------------------------------------------

function PrivacySection({ optOutTraining, setOptOutTraining, optOutResearch, setOptOutResearch }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Privacy & Data Usage</h2>
      <p className={styles.sectionDescription}>
        Control how your data is used. You can opt out of having your
        conversations used for model training or anonymized data used
        for research and publications.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <input
          type="checkbox"
          id="opt-out-training"
          checked={optOutTraining}
          onChange={(e) => setOptOutTraining(e.target.checked)}
          style={{ width: 'auto', cursor: 'pointer', margin: 0 }}
        />
        <label htmlFor="opt-out-training" style={{ marginBottom: 0, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--secondary-text-color)' }}>
          Opt out of using my conversations for model training
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="checkbox"
          id="opt-out-research"
          checked={optOutResearch}
          onChange={(e) => setOptOutResearch(e.target.checked)}
          style={{ width: 'auto', cursor: 'pointer', margin: 0 }}
        />
        <label htmlFor="opt-out-research" style={{ marginBottom: 0, cursor: 'pointer', fontSize: '0.9rem', color: 'var(--secondary-text-color)' }}>
          Opt out of using my anonymized data for research and publications
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data management section
// ---------------------------------------------------------------------------

function DataSection({ isDeletingAll, handleDeleteAllConversations, isDeletingRawAudits, handleDeleteRawAudits }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Data Management</h2>
      <p className={styles.sectionDescription}>
        Permanently delete your stored data. These actions cannot be undone.
      </p>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        alignItems: 'flex-start',
      }}>
        <button
          onClick={handleDeleteAllConversations}
          disabled={isDeletingAll}
          className={styles.button}
          style={{ backgroundColor: '#e5393f', opacity: isDeletingAll ? 0.6 : 1 }}
        >
          {isDeletingAll ? 'Deleting…' : 'Delete All Conversation History'}
        </button>
        <button
          onClick={handleDeleteRawAudits}
          disabled={isDeletingRawAudits}
          className={styles.button}
          style={{ backgroundColor: '#e5393f', opacity: isDeletingRawAudits ? 0.6 : 1 }}
        >
          {isDeletingRawAudits ? 'Deleting…' : 'Delete Raw Audit Logs'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composed Account tab
// ---------------------------------------------------------------------------

const AccountTab = ({
  API_URL,
  onLoggedOut,
  optOutTraining,
  setOptOutTraining,
  optOutResearch,
  setOptOutResearch,
  isDeletingAll,
  handleDeleteAllConversations,
  isDeletingRawAudits,
  handleDeleteRawAudits,
}) => (
  <div>
    <PrivacySection
      optOutTraining={optOutTraining}
      setOptOutTraining={setOptOutTraining}
      optOutResearch={optOutResearch}
      setOptOutResearch={setOptOutResearch}
    />
    <DataSection
      isDeletingAll={isDeletingAll}
      handleDeleteAllConversations={handleDeleteAllConversations}
      isDeletingRawAudits={isDeletingRawAudits}
      handleDeleteRawAudits={handleDeleteRawAudits}
    />
    <SessionsSection API_URL={API_URL} onLoggedOut={onLoggedOut} />
  </div>
);

export default AccountTab;
